import { type ComponentProps, memo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import {
    CloseHealth,
    type CloseHealthReason,
    closeHealthLabels,
    closeHealthReasonDescriptions,
} from "@common/types/closeHealth";
import { HealthIndicator } from "@design-system/HealthIndicator/HealthIndicator";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import type {
    ClientsWorkOverviewRow,
    ClientsWorkOverviewWorkRow,
} from "../clientsWorkOverviewViewConfig";
import { EMPTY_CELL_VALUE } from "./emptyCellValue";

/** Why the work has its health, for the hover; nothing without a reason. */
export const getHealthReasonTooltip = (
    healthReason: CloseHealthReason | null,
) => (healthReason ? closeHealthReasonDescriptions[healthReason] : undefined);

export const WorkHealthCell = memo(
    ({
        row,
    }: CellContext<ViewTableFeatures, ClientsWorkOverviewRow, CloseHealth>) => {
        if (row.original.rowType !== "work") {
            return EMPTY_CELL_VALUE;
        }
        const { health, healthReason } = row.original;
        if (health === CloseHealth.NONE) {
            return EMPTY_CELL_VALUE;
        }

        return (
            <ViewTableCell>
                <Tooltip content={getHealthReasonTooltip(healthReason)}>
                    <span>{closeHealthLabels[health]}</span>
                </Tooltip>
            </ViewTableCell>
        );
    },
);
WorkHealthCell.displayName = "WorkHealthCell";

interface WorkHealthProps {
    row: Pick<ClientsWorkOverviewWorkRow, "health" | "healthReason">;
    /** `sm` sizes the chip for a board card's tag row. */
    size?: ComponentProps<typeof HealthIndicator>["size"];
}

/** The work's health chip with its reason on hover; nothing when it has no health. */
export const WorkHealth = ({ row, size }: Readonly<WorkHealthProps>) => {
    const { health, healthReason } = row;
    if (health === CloseHealth.NONE) {
        return null;
    }
    return (
        <Tooltip content={getHealthReasonTooltip(healthReason)}>
            <HealthIndicator health={health} size={size} data-health={health} />
        </Tooltip>
    );
};
